import { useEffect, useState } from "react";
import { api, formatNaira, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Briefcase } from "lucide-react";
import { toast } from "sonner";

const STATUS_VARIANTS = { active: "success", on_hold: "warning", completed: "info", cancelled: "danger" };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", business_unit: "FigureWeb", status: "active", start_date: "", end_date: "", budget: 0 });

  const load = () => api.get("/projects").then(r => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/projects", { ...form, budget: Number(form.budget) });
    toast.success("Project created");
    setOpen(false);
    setForm({ name: "", description: "", business_unit: "FigureWeb", status: "active", start_date: "", end_date: "", budget: 0 });
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        subtitle={`${projects.length} active initiatives across FigureWeb, FigureCX and Telecom`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-project-btn">
                <Plus className="w-4 h-4 mr-1" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="project-name-input" /></div>
                <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Business Unit</Label>
                    <Select value={form.business_unit} onValueChange={v => setForm({...form, business_unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FigureWeb">FigureWeb</SelectItem>
                        <SelectItem value="FigureCX">FigureCX</SelectItem>
                        <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="on_hold">On hold</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
                  <div><Label>End</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
                  <div><Label>Budget (₦)</Label><Input type="number" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-project-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map(p => (
          <div key={p.id} className="kpi-card" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-brand-aqua-light text-brand-aqua-hover flex items-center justify-center">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium text-slate-800 dark:text-slate-100">{p.name}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{p.business_unit}</div>
                </div>
              </div>
              <Chip variant={STATUS_VARIANTS[p.status] || "default"}>{p.status.replace("_", " ")}</Chip>
            </div>
            {p.description && <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">{p.description}</div>}
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-500">Budget: </span><span className="num font-medium text-slate-900 dark:text-slate-100">{formatNaira(p.budget)}</span></div>
              <div className="text-right"><span className="text-slate-500">Ends: </span><span className="num text-slate-700 dark:text-slate-300">{formatDate(p.end_date)}</span></div>
            </div>
          </div>
        ))}
        {projects.length === 0 && <div className="col-span-full text-center py-12 text-slate-500 text-sm">No projects yet.</div>}
      </div>
    </div>
  );
}
